/** Day-range selector — drives the `days` scope shared across feature pages. */
import { useId } from "react";
import { useTranslation } from "react-i18next";

import { dayRangeOptions, type DayRange } from "@/scope/constants";

interface DayRangeSelectProps {
  value: DayRange;
  onChange: (days: DayRange) => void;
  options?: DayRange[];
  label?: string;
}

export function DayRangeSelect({
  value,
  onChange,
  options = dayRangeOptions(),
  label,
}: DayRangeSelectProps) {
  const { t } = useTranslation();
  const id = useId();
  // Keep the current value selectable even if it isn't one of the presets
  // (e.g. a deep-linked ?days=45), so the control never shows a blank selection.
  const opts = options.includes(value) ? options : [value, ...options];
  return (
    <div className="inline-flex items-center gap-2 text-sm">
      <label htmlFor={id} className="text-fg-muted">
        {label ?? t("controls.range")}
      </label>
      <select
        id={id}
        value={String(value)}
        onChange={(e) =>
          onChange(e.target.value === "all" ? "all" : Number(e.target.value))
        }
        className="rounded-md border border-border bg-bg px-2 py-1 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
      >
        {opts.map((d) => (
          <option key={String(d)} value={String(d)}>
            {d === "all" ? t("controls.allTime") : t("controls.lastDays", { count: d })}
          </option>
        ))}
      </select>
    </div>
  );
}
